// app/src/api/encounterChunaApi.ts

import { createAsyncThunk } from "@reduxjs/toolkit"
import { serverApi, handleApiResponse, handlePageResponse } from "@core/api/core"
import { EncounterChunaSerializer, EncounterChunaTaListSerializer } from "@/types/encounterItem"
import { CommonResponse, CommonTResponse, PaginationType } from "@/types/common"

export interface EncounterChunaListPageResult {
  results: EncounterChunaTaListSerializer[]
  pagination: PaginationType
}

export const getIndustrialChunaLocalCount = async (patientUuid: string, insuranceUuid: string): Promise<{ count: number }> => {
  const response = await serverApi.get("/encounter/encounter_chuna/industrial-local-count/", {
    params: { patient_uuid: patientUuid, insurance_uuid: insuranceUuid },
  })
  return handleApiResponse<{ count: number }>(response)
}

/**
 * 추나요법 정보 조회
 * @param encounterItemId EncounterItem ID (lookup_field: encounter_item_id)
 * @returns 추나요법 정보
 */
export const getEncounterChuna = async (encounterItemId: string): Promise<EncounterChunaSerializer> => {
  const response = await serverApi.get<CommonResponse>(`/encounter/encounter_chuna/${encounterItemId}/`)
  return handleApiResponse<EncounterChunaSerializer>(response)
}

export const resetEncounterChunaSubmission = async (encounterChunaUuid: string): Promise<EncounterChunaSerializer> => {
  const response = await serverApi.patch(`/encounter/encounter_chuna/${encounterChunaUuid}/reset-submission/`, {})
  return handleApiResponse<EncounterChunaSerializer>(response)
}

/**
 * 추나요법 정보 수정
 * @description detail 엔드포인트는 encounter_item_id lookup을 사용한다.
 * @param data 추나요법 정보
 * @returns 성공 여부
 */
export const patchEncounterChuna = async (data: EncounterChunaSerializer): Promise<boolean> => {
  if (!data.encounterItemId) {
    throw new Error("encounterItemId가 필요합니다.")
  }
  const response = await serverApi.patch<CommonResponse>(`/encounter/encounter_chuna/${data.encounterItemId}/`, data)
  return handleApiResponse<boolean>(response)
}

/**
 * 추나요법 정보 생성
 * @param data 추나요법 정보
 * @returns 생성된 추나요법 정보
 */
export const createEncounterChuna = async (data: EncounterChunaSerializer): Promise<EncounterChunaSerializer> => {
  const response = await serverApi.post<CommonResponse>(`/encounter/encounter_chuna/`, data)
  return handleApiResponse<EncounterChunaSerializer>(response)
}

/**
 * 추나요법 정보 삭제
 * @param encounterItemId EncounterItem ID (lookup_field: encounter_item_id)
 * @returns 성공 여부
 */
export const deleteEncounterChuna = async (encounterItemId: string): Promise<void> => {
  const response = await serverApi.delete<CommonResponse>(`/encounter/encounter_chuna/${encounterItemId}/`)
  handleApiResponse<void>(response)
}

/**
 * 환자 추나요법 목록 조회
 * @param fromDate 시작일 (YYYY-MM-DD)
 * @param toDate 종료일 (YYYY-MM-DD)
 * @param patientUuid 환자 UUID (선택)
 * @returns 환자의 EncounterChuna 목록
 */
export const getChunaList = async (fromDate: string, toDate: string, patientUuid?: string): Promise<EncounterChunaTaListSerializer[]> => {
  // params 동적 구성
  const params: any = {
    from_date: fromDate,
    to_date: toDate,
  }

  // patientUuid가 있으면 params에 추가
  if (patientUuid) {
    params.patient_uuid = patientUuid
  }

  const response = await serverApi.get<CommonTResponse<EncounterChunaTaListSerializer>>(`/encounter/encounter_chuna/list/`, {
    params,
  })
  return handleApiResponse<EncounterChunaTaListSerializer[]>(response)
}

export const getChunaListPage = async (
  fromDate: string,
  toDate: string,
  patientUuid?: string,
  page: number = 1,
  pageSize: number = 30
): Promise<EncounterChunaListPageResult> => {
  const params: Record<string, string | number> = {
    from_date: fromDate,
    to_date: toDate,
    page,
    page_size: pageSize,
  }

  if (patientUuid) {
    params.patient_uuid = patientUuid
  }

  const response = await serverApi.get<CommonTResponse<EncounterChunaTaListSerializer[]>>(`/encounter/encounter_chuna/list/`, {
    params,
  })
  return handlePageResponse<EncounterChunaTaListSerializer>(response)
}
/**
 * 추나 미제출 환자 목록 조회
 * @returns 환자의 EncounterChuna 목록
 */
export const getNotSubmittedChunaList = async (): Promise<EncounterChunaTaListSerializer[]> => {
  const response = await serverApi.get<CommonTResponse<EncounterChunaTaListSerializer>>(`/encounter/encounter_chuna/pending-patients/`)
  return handleApiResponse<EncounterChunaTaListSerializer[]>(response)
}

export const getNotSubmittedChunaListPage = async (page: number = 1, pageSize: number = 30): Promise<EncounterChunaListPageResult> => {
  const response = await serverApi.get<CommonTResponse<EncounterChunaTaListSerializer[]>>(`/encounter/encounter_chuna/pending-patients/`, {
    params: {
      page,
      page_size: pageSize,
    },
  })
  return handlePageResponse<EncounterChunaTaListSerializer>(response)
}
/**
 * 심평원 미제출 자보 추나요법 건수 조회
 * @returns 미제출 건수
 */
export const getChunasPendingCount = async (months: number = 3): Promise<number> => {
  const response = await serverApi.get<CommonResponse>(`/encounter/encounter_chuna/pending-count/`, {
    params: {
      months,
    },
  })
  return handleApiResponse<number>(response)
}

/**
 * 심평원 미제출 자보 추나요법 건수 조회 (Redux Thunk)
 */
export const fetchChunaPendingCount = createAsyncThunk<
  number, // 성공 시 반환할 타입
  void, // 인자로 받을 객체 타입
  { rejectValue: string }
>("chunaPending/fetchCount", async (_, { rejectWithValue }) => {
  try {
    const count = await getChunasPendingCount(3)
    return count
  } catch (error: any) {
    return rejectWithValue(error.message || "추나요법 미제출 건수 조회 중 오류가 발생했습니다.")
  }
})

/**
 * 추나요법 심평원 제출 상태 업데이트
 * @param encounterUuid 진료 UUID
 * @param isHiraSubmitted 제출 여부
 * @param hiraSno 심평원 일련번호 (선택)
 * @param license_no 한의사 면허번호 (선택)
 * @param hiraAcdtRcvNo 심평원 제출 사고접수번호 (선택)
 * @returns 성공 여부
 */
export const updateChunaHiraSubmitStatus = async (
  encounterUuid: string,
  isHiraSubmitted: boolean,
  hiraSno?: string | null,
  license_no?: string | null,
  hiraAcdtRcvNo?: string | null
): Promise<boolean> => {
  const requestData: any = {
    is_hira_submitted: isHiraSubmitted,
  }

  if (hiraSno !== undefined) {
    requestData.hira_sno = hiraSno
  }
  if (license_no !== undefined) {
    requestData.encounter_chuna_license_no = license_no
  }
  if (hiraAcdtRcvNo !== undefined) {
    requestData.hira_acdt_rcv_no = hiraAcdtRcvNo
  }

  const response = await serverApi.patch<CommonResponse>(`/encounter/encounter_chuna/update-by-encounter/${encounterUuid}/`, requestData)
  handleApiResponse<void>(response)
  return true
}

/**
 * 추나요법 심평원 제출 상태 일괄 업데이트
 * @param items 업데이트할 배열 (encounterUuid와 변경 필드들)
 * @returns 성공 여부
 */
export const batchUpdateChunaHiraSubmitStatus = async (
  items: Array<{
    encounterUuid: string
    encounter_chuna_license_no?: string | null
    is_hira_submitted?: boolean
    hira_sno?: string | null
    hira_acdt_rcv_no?: string | null
  }>
): Promise<boolean> => {
  const payload = {
    items: items.map(item => {
      const mappedItem: Record<string, unknown> = {
        encounter_uuid: item.encounterUuid,
      }

      if (item.encounter_chuna_license_no !== undefined) {
        mappedItem.encounter_chuna_license_no = item.encounter_chuna_license_no
      }

      if (item.is_hira_submitted !== undefined) {
        mappedItem.is_hira_submitted = item.is_hira_submitted
      }

      if (item.hira_sno !== undefined) {
        mappedItem.hira_sno = item.hira_sno
      }

      if (item.hira_acdt_rcv_no !== undefined) {
        mappedItem.hira_acdt_rcv_no = item.hira_acdt_rcv_no
      }

      return mappedItem
    }),
  }

  const response = await serverApi.patch<CommonResponse>("/encounter/encounter_chuna/batch-update-by-encounter/", payload)
  handleApiResponse<void>(response)
  return true
}
